// Folga em px abaixo da qual a sobra de rolagem e ruido de arredondamento --
// meio pixel de borda, uma fracao de zoom -- e nao overflow de verdade. Vale
// para ligar as setas e para decidir que se chegou na ponta.
const LIMIAR_ROLAGEM = 4;

// Distancia entre a aba revelada pela seta e a borda da faixa. Tem de ser maior
// que a seta (22px + 5px da borda = 27px), senao a aba que a seta acabou de
// trazer nasce por baixo dela.
const FOLGA_DA_BORDA = 34;

// Quando nao ha aba cortada para mirar (a proxima ja esta inteira a vista), a
// seta anda uma fracao da janela. Menos que 1 de proposito: rolar a largura
// cheia nao deixa nenhuma aba em comum entre o antes e o depois, e quem olha
// perde a referencia de onde estava.
const PASSO_CEGO = 0.7;

// Conversao da roda quando o evento nao vem em pixel (`deltaMode === 1`, linha
// -- o padrao do Firefox no mouse comum). Nao ha como perguntar ao documento a
// altura de linha do evento; 16px e o tamanho de fonte padrao, e e o numero que
// os proprios navegadores usam para o mesmo calculo.
const ALTURA_DE_LINHA_EM_PX = 16;

export default {
  name: 'WootTabs',
  props: {
    index: {
      type: Number,
      default: 0,
    },
    border: {
      type: Boolean,
      default: true,
    },
    // Modo deslizante da #1243, desenhado no canvas "Barra de Abas".
    //
    // É opt-in porque muda três coisas de uma vez, e só a faixa de abas da
    // conversa pediu as três: as setas de rolagem somem, o fundo da aba ativa
    // vira uma camada absoluta que DESLIZA de uma aba para a outra, e a roda do
    // mouse passa a rolar a fila na horizontal. Toda outra tela que monta
    // `woot-tabs` (contatos, empresa, histoire) segue com as setas.
    slider: {
      type: Boolean,
      default: false,
    },
    // Texto da dica que aparece ao passar o mouse, e só quando a fila não cabe
    // na largura disponível: dizer "role para ver mais" numa barra em que tudo
    // já está à vista é ruído. Vazio desliga a dica.
    hint: {
      type: String,
      default: '',
    },
    // Rotulos das setas de rolagem do modo deslizante. Vem de fora, como o
    // `hint`: este componente e generico e nao conhece namespace de traducao --
    // nenhum componente de `components/ui` chama `$t`.
    previousLabel: {
      type: String,
      default: '',
    },
    nextLabel: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      hasScroll: false,
      isHovering: false,
      scrollLeft: 0,
      scrollMax: 0,
      indicator: { left: 0, top: 0, width: 0, height: 0, ready: false },
    };
  },
  computed: {
    showHint() {
      return Boolean(
        this.slider && this.hint && this.isHovering && this.hasScroll
      );
    },
    // As setas SOMEM na ponta, em vez de ficarem desabilitadas: seta apagada
    // que nao faz nada e um convite a clicar duas vezes para conferir. O preco
    // e conhecido -- quem estava com o foco na seta perde o foco quando ela sai
    // do DOM -- e a rolagem por teclado continua existindo pelas proprias abas.
    canScrollLeft() {
      return this.slider && this.hasScroll && this.scrollLeft > LIMIAR_ROLAGEM;
    },
    canScrollRight() {
      return (
        this.slider &&
        this.hasScroll &&
        this.scrollLeft < this.scrollMax - LIMIAR_ROLAGEM
      );
    },
  },
  watch: {
    index() {
      this.$nextTick(this.syncSlider);
    },
  },
  created() {
    window.addEventListener('resize', this.onResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
    this.unobserveScroller();
  },
  mounted() {
    this.onResize();
    this.observeScroller();
  },
  // As abas entram e saem por permissão (`canViewTaskTab`) e os rótulos
  // dependem do locale, que chega por fetch — os dois mudam a largura da fila
  // depois do mount. Sem remedir aqui, o fundo deslizante fica onde a aba
  // estava antes da lista mudar.
  updated() {
    this.onResize();
  },
  methods: {
    onResize() {
      this.measureScroll();
      this.updateIndicator();
    },
    scroller() {
      return this.$el.getElementsByClassName('tabs')[0];
    },
    // Uma medida so para as tres perguntas -- ha overflow? quanto ja rolou?
    // quanto falta? -- porque as tres saem do mesmo `layout` do navegador, e
    // ler `scrollWidth` tres vezes seguidas custa tres reflows.
    //
    // A comparacao antes de atribuir nao e enfeite: este metodo roda no
    // `updated()` e no ResizeObserver, e escrever em `data` sem ela reabriria o
    // ciclo de render.
    measureScroll() {
      const tabElement = this.scroller();
      if (!tabElement) {
        return;
      }

      const max = Math.max(0, tabElement.scrollWidth - tabElement.clientWidth);
      const left = tabElement.scrollLeft;
      const hasScroll = max > LIMIAR_ROLAGEM;

      if (hasScroll !== this.hasScroll) {
        this.hasScroll = hasScroll;
      }
      if (max !== this.scrollMax) {
        this.scrollMax = max;
      }
      if (left !== this.scrollLeft) {
        this.scrollLeft = left;
      }
    },
    // O `scroll` do trilho nao borbulha, e a largura da faixa muda sem que a
    // janela mude de tamanho (o painel de detalhes abre, a lista de conversas
    // colapsa) -- por isso o ResizeObserver nos DOIS: o container manda na
    // largura visivel, a trilha manda na largura do conteudo.
    observeScroller() {
      const tabElement = this.scroller();
      if (!tabElement) {
        return;
      }

      tabElement.addEventListener('scroll', this.onResize, { passive: true });
      this.observedScroller = tabElement;

      if (typeof ResizeObserver === 'undefined') {
        return;
      }

      this.resizeObserver = new ResizeObserver(() => this.onResize());
      this.resizeObserver.observe(this.$el);
      this.resizeObserver.observe(tabElement);
    },
    unobserveScroller() {
      if (this.observedScroller) {
        this.observedScroller.removeEventListener('scroll', this.onResize);
        this.observedScroller = null;
      }
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
    },
    // Quem pediu menos movimento leva salto instantaneo, e vale para as setas e
    // para o `revealActiveTab`: as duas rolam a mesma faixa.
    scrollBehavior() {
      try {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches
          ? 'auto'
          : 'smooth';
      } catch (error) {
        return 'smooth';
      }
    },
    // A seta NAO troca de aba -- so rola a faixa. Ela mira a proxima aba
    // cortada naquele sentido e a encosta na borda oposta com folga; e o
    // movimento que responde "o que tem ali do lado?", que e a pergunta de quem
    // clica. Rolar um tanto fixo cortaria aba no meio a cada clique.
    onNavClick(direction) {
      const sc = this.scroller();
      if (!sc) {
        return;
      }

      const paraDireita = direction === 'right';
      const inicio = sc.scrollLeft;
      const fim = inicio + sc.clientWidth;
      const abas = Array.prototype.slice.call(
        sc.getElementsByClassName('tabs-title')
      );

      // O 1px de tolerancia e o mesmo motivo do LIMIAR: borda fracionaria faz
      // uma aba inteira parecer cortada por 0,4px.
      const cortada = paraDireita
        ? abas.find(aba => aba.offsetLeft + aba.offsetWidth > fim + 1)
        : abas.reverse().find(aba => aba.offsetLeft < inicio - 1);

      let alvo;
      if (cortada && paraDireita) {
        alvo =
          cortada.offsetLeft +
          cortada.offsetWidth -
          sc.clientWidth +
          FOLGA_DA_BORDA;
      } else if (cortada) {
        alvo = cortada.offsetLeft - FOLGA_DA_BORDA;
      } else {
        const passo = sc.clientWidth * PASSO_CEGO;
        alvo = paraDireita ? inicio + passo : inicio - passo;
      }

      const max = Math.max(0, sc.scrollWidth - sc.clientWidth);
      sc.scrollTo({
        top: 0,
        left: Math.min(max, Math.max(0, alvo)),
        behavior: this.scrollBehavior(),
      });
    },
    // Medido do DOM, e não calculado: os rótulos têm larguras diferentes e
    // nenhuma fração do trilho descreve onde uma aba começa nem quanto ela mede.
    // A comparação antes de atribuir não é enfeite — este método roda no
    // `updated()`, e escrever em `data` sem ela reabriria o ciclo de render.
    updateIndicator() {
      if (!this.slider) {
        return;
      }

      const active = this.$el.querySelector('.tabs-title.is-active');
      if (!active) {
        return;
      }

      const next = {
        left: active.offsetLeft,
        top: active.offsetTop,
        width: active.offsetWidth,
        height: active.offsetHeight,
        ready: true,
      };
      const current = this.indicator;
      const igual =
        current.ready &&
        current.left === next.left &&
        current.top === next.top &&
        current.width === next.width &&
        current.height === next.height;

      if (!igual) {
        this.indicator = next;
      }
    },
    // Trazer a aba escolhida para dentro da janela de rolagem. Sem isto, clicar
    // numa aba meio cortada deixa o fundo deslizar para fora da vista.
    revealActiveTab() {
      const sc = this.scroller();
      const active = this.$el.querySelector('.tabs-title.is-active');
      if (!sc || !active) {
        return;
      }

      const margem = 8;
      const inicio = active.offsetLeft - margem;
      const fim = active.offsetLeft + active.offsetWidth + margem;
      let left = sc.scrollLeft;

      if (inicio < sc.scrollLeft) {
        left = inicio;
      } else if (fim > sc.scrollLeft + sc.clientWidth) {
        left = fim - sc.clientWidth;
      }

      if (left !== sc.scrollLeft) {
        sc.scrollTo({ top: 0, left, behavior: this.scrollBehavior() });
      }
    },
    syncSlider() {
      this.updateIndicator();
      this.revealActiveTab();
    },
    // A roda vertical rola a fila na horizontal — é o gesto que o mouse comum
    // oferece numa barra que só tem um eixo. O eixo de maior deslocamento vence,
    // para não brigar com o trackpad, que manda os dois.
    //
    // O `preventDefault` só sai quando a fila REALMENTE vai andar: engolir o
    // evento nas pontas tiraria a rolagem da conversa embaixo de quem passou o
    // mouse por cima da barra sem querer.
    onWheel(event) {
      const sc = this.scroller();
      if (!this.slider || !sc) {
        return;
      }

      const eixo =
        Math.abs(event.deltaY) > Math.abs(event.deltaX)
          ? event.deltaY
          : event.deltaX;
      if (!eixo) {
        return;
      }

      const max = sc.scrollWidth - sc.clientWidth;
      if (max <= 0) {
        return;
      }

      // `deltaY` nem sempre vem em pixel: o `deltaMode` diz a unidade, e o
      // Firefox manda 1 (LINHA) na roda de mouse comum -- tres linhas por
      // entalhe viravam TRES PIXELS somados ao `scrollLeft`, ou seja, a barra
      // nao andava. 2 (PAGINA) aparece no Page Up/Down de alguns ambientes.
      // 16px e a altura de linha padrao do documento; a pagina, aqui, e a
      // largura visivel da fila.
      let unidade = 1;
      if (event.deltaMode === 1) {
        unidade = ALTURA_DE_LINHA_EM_PX;
      } else if (event.deltaMode === 2) {
        unidade = sc.clientWidth;
      }
      const delta = eixo * unidade;

      const nextLeft = Math.min(max, Math.max(0, sc.scrollLeft + delta));
      if (nextLeft === sc.scrollLeft) {
        return;
      }

      event.preventDefault();
      sc.scrollLeft = nextLeft;
    },
    onScrollClick(direction) {
      const tabElement = this.scroller();
      let scrollPosition = tabElement.scrollLeft;
      if (direction === 'left') {
        scrollPosition -= 100;
      } else {
        scrollPosition += 100;
      }
      tabElement.scrollTo({
        top: 0,
        left: scrollPosition,
        behavior: 'smooth',
      });
    },
    createScrollButton(createElement, direction) {
      if (!this.hasScroll || this.slider) {
        return false;
      }
      return createElement(
        'button',
        {
          class: 'tabs--scroll-button button clear secondary button--only-icon',
          on: { click: () => this.onScrollClick(direction) },
        },
        [
          createElement('fluent-icon', {
            props: { icon: `chevron-${direction}`, size: 16 },
          }),
        ]
      );
    },
    // A seta de rolagem do modo deslizante. Sobreposta ao trilho (absoluta
    // dentro do container relativo), entao ligar e desligar nao empurra as abas
    // de lugar -- o recuo lateral da faixa e o MESMO com e sem seta, e a unica
    // diferenca na tela estreita e a seta existir.
    //
    // Ela passa POR CIMA da aba cortada da ponta, e e para isso que existe o
    // degrade: a aba some por baixo do botao em vez de bater nele. Reservar
    // lugar para a seta com recuo na trilha foi tentado e desfeito -- abria um
    // buraco na esquerda que a faixa larga nao tem.
    //
    // `<button type="button">` de verdade, e nao um `<span>` com `@click`: e o
    // que traz foco por teclado, Enter/Espaco e o papel `button` para o leitor
    // de tela de graca. O chevron e desenhado em CSS (um quadrado com duas
    // bordas, girado) porque `dashboard-icons.json` nao tem chevron de 6px e
    // encolher o de 16 deixa o traco esmaecido.
    createNavButton(createElement, direction) {
      const visivel =
        direction === 'left' ? this.canScrollLeft : this.canScrollRight;
      if (!visivel) {
        return false;
      }

      const rotulo = direction === 'left' ? this.previousLabel : this.nextLabel;
      return createElement(
        'button',
        {
          class: `tabs--nav tabs--nav--${direction}`,
          attrs: {
            type: 'button',
            'aria-label': rotulo || false,
            title: rotulo || false,
          },
          on: { click: () => this.onNavClick(direction) },
        },
        [
          createElement('span', {
            class: 'tabs--nav__chevron',
            attrs: { 'aria-hidden': 'true' },
          }),
        ]
      );
    },
    // O esmaecido sob a seta. Ele nao e enfeite: sem ele a aba passa INTEIRA
    // por tras do botao e reaparece do outro lado, e a faixa parece ter um furo
    // no lugar da seta. Com o degrade, a aba some gradualmente -- que e o que
    // diz "tem mais coisa deste lado".
    createFade(createElement, direction) {
      const visivel =
        direction === 'left' ? this.canScrollLeft : this.canScrollRight;
      if (!visivel) {
        return false;
      }

      return createElement('span', {
        class: `tabs--fade tabs--fade--${direction}`,
        attrs: { 'aria-hidden': 'true' },
      });
    },
    createIndicator(createElement) {
      if (!this.slider) {
        return false;
      }

      const { left, top, width, height, ready } = this.indicator;
      return createElement('span', {
        class: 'tabs--indicator',
        attrs: { 'aria-hidden': 'true' },
        style: {
          left: `${left}px`,
          top: `${top}px`,
          width: `${width}px`,
          height: `${height}px`,
          opacity: ready ? 1 : 0,
        },
      });
    },
    // O mouse desenhado à mão, e não um ícone: o ponto dentro dele precisa ser
    // um elemento próprio para subir e descer sozinho, e nenhum glifo do
    // `dashboard-icons.json` tem essa peça separada.
    createHint(createElement) {
      if (!this.showHint) {
        return false;
      }

      return createElement(
        'div',
        { class: 'tabs--hint', attrs: { role: 'tooltip' } },
        [
          createElement('span', { class: 'tabs--hint__mouse' }, [
            createElement('span', { class: 'tabs--hint__wheel' }),
          ]),
          createElement('span', {}, this.hint),
        ]
      );
    },
  },
  render(createElement) {
    const Tabs = this.$slots.default
      .filter(
        node =>
          node.componentOptions &&
          node.componentOptions.tag === 'woot-tabs-item'
      )
      .map((node, index) => {
        const data = node.componentOptions.propsData;
        data.index = index;
        return node;
      });
    const leftButton = this.createScrollButton(createElement, 'left');
    const rightButton = this.createScrollButton(createElement, 'right');

    return createElement(
      'div',
      {
        class: {
          'tabs--container--with-border': this.border,
          'tabs--container': true,
          'tabs--container--slider': this.slider,
        },
        on: this.slider
          ? {
              mouseenter: () => {
                this.isHovering = true;
              },
              mouseleave: () => {
                this.isHovering = false;
              },
            }
          : {},
      },
      [
        leftButton,
        createElement(
          'ul',
          {
            class: {
              tabs: true,
              'tabs--with-scroll': this.hasScroll && !this.slider,
              'tabs--slider': this.slider,
            },
            // `tablist` para o leitor de tela anunciar "aba 2 de 6" em vez de
            // "item de lista". Os filhos `<li>` entram como `presentation` e o
            // `role="tab"` fica no proprio clicavel -- ver `TabsItem.vue`.
            attrs: { role: 'tablist' },
            on: this.slider ? { wheel: this.onWheel } : {},
          },
          [this.createIndicator(createElement), ...Tabs]
        ),
        rightButton,
        this.createFade(createElement, 'left'),
        this.createFade(createElement, 'right'),
        this.createNavButton(createElement, 'left'),
        this.createNavButton(createElement, 'right'),
        this.createHint(createElement),
      ]
    );
  },
};
